import React, { useState, useLayoutEffect, useRef } from 'react';
import SEO from '../components/SEO';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  Expand, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Image as ImageIcon, 
  Sparkles,
  Camera
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const GalleryPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);
  const containerRef = useRef(null);

  const categories = ['All', 'Clinical Spaces', 'Surgery & Diagnostics', 'Patients', 'Logos'];

  const images = [
    // Logos
    {
      src: "/images/logo-pet.png",
      alt: "Dr. Suresh Pet Care Official Logo",
      category: "Logos",
      title: "Clinic Identity"
    },
    {
      src: "/images/logo-trasparent.png",
      alt: "Dr. Suresh Pet Care Branding",
      category: "Logos",
      title: "Legacy Branding"
    },
    
    // Clinical Spaces
    {
      src: "/images/clinic/clinic_exterior.png",
      alt: "Dr. Suresh Pet Care Hospital Exterior",
      category: "Clinical Spaces",
      title: "Hospital Façade"
    },
    {
      src: "/images/clinic/clinic_entrance.png",
      alt: "Main Clinic Entrance",
      category: "Clinical Spaces",
      title: "Main Entrance"
    },
    {
      src: "/images/clinic/pharmacy_shop.png",
      alt: "In-house Pharmacy",
      category: "Clinical Spaces",
      title: "Veterinary Pharmacy"
    },
    {
      src: "/images/clinic/dr_suresh_consultation.png",
      alt: "Expert Consultation Room",
      category: "Clinical Spaces",
      title: "Consultation Unit"
    },

    // Surgery & Diagnostics
    {
      src: "/images/services/surgical_theater.png",
      alt: "Modern Surgical Theatre",
      category: "Surgery & Diagnostics",
      title: "Surgical Theatre"
    },
    {
      src: "/images/clinic/surgical_equipment.png",
      alt: "Advanced Surgical Instrumentation",
      category: "Surgery & Diagnostics",
      title: "Precision Equipment"
    },
    {
      src: "/images/ultra-sound.jpeg",
      alt: "Advanced Ultrasound Imaging",
      category: "Surgery & Diagnostics",
      title: "Diagnostic Imaging"
    },
    {
      src: "/images/lsb-machine.jpeg",
      alt: "Laboratory Diagnostics Machine",
      category: "Surgery & Diagnostics",
      title: "Medical Laboratory"
    },
    {
      src: "/images/WhatsApp Image 2026-01-23 at 11.59.21 PM.jpeg",
      alt: "Clinical X-Ray Setup",
      category: "Surgery & Diagnostics",
      title: "Digital Radiography"
    },

    // Patients & Action
    {
      src: "/images/puppy.jpeg",
      alt: "Puppy Health Checkup",
      category: "Patients",
      title: "Patient Wellness"
    },
    {
      src: "/images/doctor.png",
      alt: "Dr. T Suresh Babu in Action",
      category: "Patients",
      title: "Expert Care"
    },
    {
      src: "/images/WhatsApp Image 2026-01-23 at 11.58.30 PM.jpeg",
      alt: "Post-Operative Recovery Care",
      category: "Patients",
      title: "Recovery Monitoring"
    },
    {
      src: "/images/clinic/grooming_services.png",
      alt: "Professional Pet Grooming Center",
      category: "Clinical Spaces",
      title: "Grooming Sanctuary"
    }
  ];

  const filteredImages = activeCategory === 'All' 
    ? images 
    : images.filter(img => img.category === activeCategory);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.gallery-reveal', 
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.12, duration: 1, ease: 'power3.out', delay: 0.2 }
      );

      gsap.fromTo('.gallery-image-card', 
        { y: 40, opacity: 0, scale: 0.95 },
        { 
          y: 0, opacity: 1, scale: 1, stagger: 0.08, duration: 0.8, ease: 'power2.out',
          scrollTrigger: { trigger: '.gallery-grid', start: 'top 85%' } 
        }
      );
    }, containerRef);
    return () => ctx.revert();
  }, [activeCategory]);

  const handlePrev = () => {
    const currentIndex = images.findIndex(img => img.src === selectedImage.src);
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    setSelectedImage(images[prevIndex]);
  };

  const handleNext = () => {
    const currentIndex = images.findIndex(img => img.src === selectedImage.src);
    const nextIndex = (currentIndex + 1) % images.length;
    setSelectedImage(images[nextIndex]);
  };

  return (
    <>
      <SEO 
        title="Visual Gallery | Clinical Excellence at Dr. Suresh Pet Care"
        description="Explore our state-of-the-art veterinary facilities, surgical theaters, and expert clinical teams through our visual gallery in Hyderabad."
        canonical="/gallery"
      />
      <motion.div 
        ref={containerRef}
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }}
        className="bg-white min-h-screen pt-[140px] md:pt-[160px] pb-32"
      >
      <div className="section-container">
        {/* Header Section */}
        <div className="max-w-4xl space-y-8 mb-20">
          <div className="gallery-reveal inline-flex items-center gap-2 px-4 py-2 bg-primary/5 rounded-full border border-primary/10">
            <Camera size={13} className="text-primary" />
            <span className="text-[10px] font-black tracking-[0.25em] uppercase text-primary">Visual Sanctuary</span>
          </div>
          <h1 className="gallery-reveal text-navy font-display font-bold leading-[0.92] tracking-tight text-[clamp(2.5rem,7vw,5.5rem)] md:text-5xl lg:text-7xl">
            Clinical Gallery & <br /><em className="font-normal text-primary not-italic">Visual Excellence.</em>
          </h1>
          <p className="gallery-reveal text-luxury text-xl md:text-2xl leading-relaxed max-w-2xl font-medium">
            Explore our state-of-the-art facilities, specialized medical units, and the compassionate environment where we care for your pets.
          </p>
        </div>

        {/* Filter Navigation */}
        <div className="gallery-reveal flex flex-wrap gap-3 mb-16 border-b border-navy/5 pb-8">
           {categories.map((cat) => (
             <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-8 py-3 rounded-full text-xs font-black uppercase tracking-widest transition-all duration-500 border ${
                activeCategory === cat 
                ? 'bg-navy text-white border-navy shadow-xl shadow-navy/20 -translate-y-1' 
                : 'bg-transparent text-navy/40 border-navy/10 hover:border-primary hover:text-primary'
              }`}
             >
               {cat}
             </button>
           ))}
        </div>

        {/* Gallery Grid */}
        <div className="gallery-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <AnimatePresence mode="popLayout">
            {filteredImages.map((image, i) => (
              <motion.div
                layout
                key={image.src}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="gallery-image-card group relative aspect-[4/5] rounded-[3rem] overflow-hidden border border-navy/5 shadow-sm hover:shadow-2xl transition-all duration-700 cursor-pointer bg-slate-50"
                onClick={() => setSelectedImage(image)}
              >
                <img 
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-110"
                />
                
                {/* Overlay Details */}
                <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 flex flex-col justify-end p-10">
                   <p className="text-[10px] font-black uppercase tracking-[0.3em] text-primary mb-2 translate-y-4 group-hover:translate-y-0 transition-all duration-700 delay-75">{image.category}</p>
                   <h3 className="text-white text-2xl font-bold translate-y-4 group-hover:translate-y-0 transition-all duration-700 delay-100">{image.title}</h3>
                   <div className="mt-6 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white scale-0 group-hover:scale-100 transition-transform duration-700 delay-150 shadow-xl">
                      <Expand size={20} />
                   </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {filteredImages.length === 0 && (
          <div className="py-32 text-center">
            <ImageIcon size={64} className="mx-auto text-navy/10 mb-6" />
            <h3 className="text-2xl font-bold text-navy/40 uppercase tracking-widest">No images found in this category</h3>
          </div>
        )}
      </div>

      {/* Premium Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/95 backdrop-blur-2xl p-4 md:p-12 overflow-hidden"
          >
             <button 
              onClick={() => setSelectedImage(null)}
              className="absolute top-10 right-10 w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all z-[2100]"
             >
                <X size={28} />
             </button>

             <div className="relative w-full h-full flex items-center justify-center">
                {/* Control Buttons */}
                <button 
                  onClick={handlePrev}
                  className="absolute left-4 md:left-10 w-20 h-full flex items-center justify-center text-white/20 hover:text-white transition-all z-[2100]"
                >
                  <ChevronLeft size={60} strokeWidth={1} />
                </button>
                <button 
                  onClick={handleNext}
                  className="absolute right-4 md:right-10 w-20 h-full flex items-center justify-center text-white/20 hover:text-white transition-all z-[2100]"
                >
                  <ChevronRight size={60} strokeWidth={1} />
                </button>

                <motion.div
                  key={selectedImage.src}
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="relative max-w-7xl max-h-full flex flex-col items-center gap-8"
                >
                   <img 
                    src={selectedImage.src}
                    alt={selectedImage.alt}
                    className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-[0_40px_100px_rgba(0,0,0,0.8)] border border-white/10"
                   />
                   <div className="text-center space-y-3">
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 rounded-full border border-primary/20">
                        <Sparkles size={12} className="text-primary" />
                        <span className="text-[9px] font-black tracking-widest uppercase text-primary">{selectedImage.category}</span>
                      </div>
                      <h4 className="text-white text-3xl md:text-5xl font-bold tracking-tight">{selectedImage.title}</h4>
                      <p className="text-white/40 text-sm max-w-xl italic">Clinical Excellence Captured at Dr. Suresh Pet Care Surgical Center</p>
                   </div>
                </motion.div>
             </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
    </>
  );
};

export default GalleryPage;
